import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, Radio, ArrowUpRight } from "lucide-react";
import { TRANSMISSIONS } from "../data/content";
import { SectionLabel, Reveal } from "./Reveal";

export default function Transmissions() {
  const [unlocked, setUnlocked] = useState(false);

  return (
    <section id="transmissions" className="relative w-full py-24 md:py-36 px-5 md:px-10 overflow-hidden" data-testid="section-transmissions">
      {/* signal backdrop */}
      <div className="absolute inset-0 opacity-[0.15] pointer-events-none" aria-hidden>
        <div className="absolute left-0 right-0 top-1/2 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
      </div>

      <div className="max-w-[1200px] mx-auto relative">
        <SectionLabel index="05 / TRANSMISSIONS" id="transmissions-label">
          <span className="flex items-center gap-2">
            <Radio size={11} className="animate-pulse" /> LIVE FEED
          </span>
        </SectionLabel>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <h2 className="font-display chrome-text text-5xl sm:text-6xl md:text-8xl leading-[0.9]">
            <Reveal text="TRANSMISSIONS" />
          </h2>
          <p className="font-body text-white/50 max-w-sm text-sm md:text-base">
            The signal keeps moving. Snippets, messages and drops appear here. Return often.
          </p>
        </div>

        <div className="space-y-4">
          {TRANSMISSIONS.map((t, i) => {
            const locked = t.locked && !unlocked;
            return (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-6% 0px" }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`transmission-${t.id}`}
                className="group relative grid md:grid-cols-12 items-center gap-4 md:gap-6 px-5 md:px-8 py-6 md:py-7 glass hover:bg-white/[0.04] transition-colors duration-500"
              >
                <div className="md:col-span-2 flex items-center gap-3">
                  <span className={`track-label ${locked ? "text-white/30" : "text-[color:var(--silver)]"}`}>{t.code}</span>
                </div>
                <div className="md:col-span-2">
                  <span className={`text-[0.6rem] font-mono2 tracking-widest ${t.locked ? "text-white/40" : "text-emerald-300/60"}`}>
                    {t.date}
                  </span>
                </div>
                <div className="md:col-span-8 flex items-center justify-between gap-4">
                  <div className={locked ? "blur-[6px] select-none" : ""}>
                    <h3 className="font-body text-white/90 text-base md:text-lg">{t.title}</h3>
                    <p className="font-body text-white/45 text-sm mt-1">{t.desc}</p>
                  </div>
                  {locked ? (
                    <Lock size={18} className="shrink-0 text-white/40" />
                  ) : (
                    <ArrowUpRight size={18} className="shrink-0 text-white/40 group-hover:text-white group-hover:rotate-45 transition-all duration-500" />
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {!unlocked && (
          <motion.div
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-5 text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <p className="track-label text-white/40">ENCRYPTED CONTENT DETECTED</p>
            <button
              onClick={() => setUnlocked(true)}
              data-testid="transmissions-unlock"
              data-cursor
              className="px-8 py-3.5 border border-white/20 hover:border-white hover:bg-white hover:text-black transition-all duration-400 track-label"
            >
              UNLOCK THE SIGNAL
            </button>
          </motion.div>
        )}
      </div>
    </section>
  );
}
